'use client';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { ElasticListVehicle } from '@//app/api/search/vehicles';
import type { ProductCardDetailsProps } from '@//components/ProductCard';
import type { Locale } from '@//config/domain-locale-map';
import { useMarket } from '@//providers/MarketSettingsProvider';
import { formatNumber } from '@//utils/number';
import { mileageToOdometer } from '@//utils/odometer';

const useProductCardContainerDetails = (item: ElasticListVehicle) => {
  const locale = useLocale() as Locale;
  const market = useMarket();
  const tOdometerReading = useTranslations('utils.vehicleGlossary.odometerReading');

  const getOdometerDetail = () => {
    if (item.odometerReading) {
      return {
        label: tOdometerReading('label'),
        value: `${formatNumber(item.odometerReading.value, { locale })} ${tOdometerReading(
          `units.${item.odometerReading.unit}`,
          { market, locale },
        )}`,
      };
    }
    if (item.mileage != null) {
      const odometer = mileageToOdometer(item.mileage, { market, locale });
      return {
        label: tOdometerReading('label'),
        value: `${formatNumber(odometer.value, { locale })} ${tOdometerReading(
          `units.${odometer.unit}`,
        )}`,
      };
    }
    return undefined;
  };

  const details = useMemo(() => {
    const d: ProductCardDetailsProps[] = [];

    if (item.fuelTypes || item.fuelType) {
      d.push({
        label: 'Fuel Type',
        value: item.fuelTypes || item.fuelType,
      });
    }

    const odometerDetail = getOdometerDetail();
    if (odometerDetail) d.push(odometerDetail);

    if (item.modelYear) {
      d.push({
        label: 'Model Year',
        value: `${item.modelYear}`,
      });
    }

    if (item.gearboxType) {
      d.push({
        label: 'Gearbox Type',
        value: item.gearboxType,
      });
    }

    return d;
  }, [
    item.fuelTypes,
    item.fuelType,
    item.odometerReading,
    item.mileage,
    item.modelYear,
    item.gearboxType,
    market,
    locale,
    tOdometerReading,
  ]);

  return details;
};

export default useProductCardContainerDetails;
